import { createSlice } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '../../../shared/constants/loadingStatus';
import { CAMPAIGN_CADENCES_TYPE_TAB_KEY } from '../constants/campaignCadencesTabs';
import { getCampaignCadenceKey } from '../lib/getCampaignCadenceKey';
import {
  createMessageTemplate,
  createMessageTemplateForTenants,
  setMessageTemplateStatus,
  updateMessageTemplate,
} from '../../templates/store/templatesThunks';

const initialState = {
  campaigns: [],
  campaignsStatus: LOADING_STATUS.IDLE,
  selectedCampaignId: null,
  selectedChannel: 'email',
  selectedTypeTab: CAMPAIGN_CADENCES_TYPE_TAB_KEY.MARKETING,
  cachedCampaignCadences: {},
  campaignCadenceStatuses: {},
  reorderStatus: LOADING_STATUS.IDLE,
  error: null,
};

export const campaignCadencesSlice = createSlice({
  name: 'campaignCadences',
  initialState,
  reducers: {
    campaignCadencesCampaignsRequested(state) {
      state.campaignsStatus = LOADING_STATUS.LOADING;
    },
    campaignCadencesCampaignsReceived(state, action) {
      state.campaignsStatus = LOADING_STATUS.SUCCEEDED;
      state.campaigns = action.payload ?? [];
    },
    campaignCadencesCampaignsFailed(state) {
      state.campaignsStatus = LOADING_STATUS.FAILED;
    },
    campaignCadencesTypeTabChanged(state, action) {
      state.selectedTypeTab = action.payload;
    },
    campaignCadencesCampaignSelected(state, action) {
      state.selectedCampaignId = action.payload;
    },
    campaignCadencesChannelChanged(state, action) {
      state.selectedChannel = action.payload;
    },
    campaignCadencesRequested(state, action) {
      state.campaignCadenceStatuses[action.payload] = LOADING_STATUS.LOADING;
      state.error = null;
    },
    campaignCadencesReceived(state, action) {
      const { key, data } = action.payload;
      state.campaignCadenceStatuses[key] = LOADING_STATUS.SUCCEEDED;
      state.cachedCampaignCadences[key] = data;
    },
    campaignCadencesFailed(state, action) {
      const { key, error } = action.payload;
      state.campaignCadenceStatuses[key] = LOADING_STATUS.FAILED;
      state.error = error ?? null;
    },
    campaignCadencesReorderRequested(state) {
      state.reorderStatus = LOADING_STATUS.LOADING;
    },
    campaignCadencesReorderSucceeded(state, action) {
      state.reorderStatus = LOADING_STATUS.SUCCEEDED;
      const key = getCampaignCadenceKey({
        typeTab: state.selectedTypeTab,
        campaignId: state.selectedCampaignId,
        channel: state.selectedChannel,
      });
      state.cachedCampaignCadences[key] = action.payload;
    },
    campaignCadencesReorderFailed(state, action) {
      state.reorderStatus = LOADING_STATUS.FAILED;
      state.error = action.payload ?? null;
    },
  },
  extraReducers: (builder) => {
    const invalidateCampaignCadences = (state) => {
      state.campaignCadenceStatuses = {};
    };
    builder
      .addCase(createMessageTemplate.fulfilled, invalidateCampaignCadences)
      .addCase(createMessageTemplateForTenants.fulfilled, invalidateCampaignCadences)
      .addCase(setMessageTemplateStatus.fulfilled, invalidateCampaignCadences)
      .addCase(updateMessageTemplate.fulfilled, (state, action) => {
        if ('campaigns' in (action.meta.arg.payload ?? {})) {
          state.campaignCadenceStatuses = {};
        }
      });
  },
});

export const {
  campaignCadencesCampaignsRequested,
  campaignCadencesCampaignsReceived,
  campaignCadencesCampaignsFailed,
  campaignCadencesTypeTabChanged,
  campaignCadencesCampaignSelected,
  campaignCadencesChannelChanged,
  campaignCadencesRequested,
  campaignCadencesReceived,
  campaignCadencesFailed,
  campaignCadencesReorderRequested,
  campaignCadencesReorderSucceeded,
  campaignCadencesReorderFailed,
} = campaignCadencesSlice.actions;

export const selectCampaignCadencesSelectedTypeTab = (state) =>
  state.campaignCadences.selectedTypeTab;
export const selectCampaignCadencesCampaigns = (state) => state.campaignCadences.campaigns;
export const selectCampaignCadencesCampaignsStatus = (state) =>
  state.campaignCadences.campaignsStatus;
export const selectCampaignCadencesSelectedCampaignId = (state) =>
  state.campaignCadences.selectedCampaignId;
export const selectCampaignCadencesSelectedChannel = (state) =>
  state.campaignCadences.selectedChannel;
export const selectCampaignCadenceReorderStatus = (state) => state.campaignCadences.reorderStatus;
export const selectCampaignCadencesError = (state) => state.campaignCadences.error;

export const selectCampaignCadence = (state) => {
  const key = getCampaignCadenceKey({
    typeTab: state.campaignCadences.selectedTypeTab,
    campaignId: state.campaignCadences.selectedCampaignId,
    channel: state.campaignCadences.selectedChannel,
  });
  return state.campaignCadences.cachedCampaignCadences[key] ?? null;
};

export const selectCampaignCadenceStatus = (state) => {
  const key = getCampaignCadenceKey({
    typeTab: state.campaignCadences.selectedTypeTab,
    campaignId: state.campaignCadences.selectedCampaignId,
    channel: state.campaignCadences.selectedChannel,
  });
  return state.campaignCadences.campaignCadenceStatuses[key] ?? LOADING_STATUS.IDLE;
};

export default campaignCadencesSlice.reducer;
